import React, { useState, useEffect } from "react";
import { Laptop, ShoppingBag, Heart, Coffee, Shirt, Film, ChevronLeft, ChevronRight } from "lucide-react";
import { industriesServed } from "../data/mock";

const iconMap = {
  Laptop: Laptop,
  ShoppingBag: ShoppingBag,
  Heart: Heart,
  Coffee: Coffee,
  Shirt: Shirt,
  Film: Film
};

const IndustriesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % industriesServed.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + industriesServed.length) % industriesServed.length);
  };

  return (
    <section id="industries" className="section-padding bg-white">
      <div className="container-custom px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">
            Industries We Serve
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Trademark Protection Across All Industries
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            No matter what industry your business operates in, we have the expertise to protect your brand. 
            Our team has successfully registered trademarks for businesses across diverse sectors, from technology 
            startups to established manufacturing companies.
          </p>
        </div>

        {/* Desktop Grid */}
        {!isMobile && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industriesServed.map((industry, index) => {
              const IconComponent = iconMap[industry.icon];
              return (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 card-hover"
                >
                  {/* Icon */}
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#1e3a8a] transition-colors duration-300">
                    {industry.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {industry.description}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* Mobile Carousel */}
        {isMobile && (
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {industriesServed.map((industry, index) => {
                  const IconComponent = iconMap[industry.icon];
                  return (
                    <div key={index} className="w-full flex-shrink-0 px-4">
                      <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                          {industry.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {industry.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Navigation */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div className="flex justify-center mt-8 space-x-2">
              {industriesServed.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index ? "bg-[#1e3a8a] w-8" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Additional Content */}
        <div className="mt-16 max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Industry-Specific Expertise You Can Trust
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our trademark attorneys and IP specialists have extensive experience working with businesses across all 
            major industries. We understand the unique challenges and requirements of different sectors, from the 
            fast-paced world of technology to the creative demands of fashion and entertainment.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Whether you're launching a new product line, expanding into new markets, or protecting an established 
            brand, we tailor our trademark services to meet your specific industry needs. Our comprehensive approach 
            ensures your trademark application accounts for industry-specific considerations and potential conflicts.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Don't see your industry listed? We work with businesses of all types and sizes. Contact us today to 
            discuss how we can protect your brand, regardless of your industry or business model.
          </p>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
